import { useNavigate } from "react-router-dom";
import type { BlogPost } from "../../../mocks/blogData";

interface BlogCardProps {
  post: BlogPost;
}

export default function BlogCard({ post }: BlogCardProps) {
  const navigate = useNavigate();

  return (
    <article
      className="border border-[#2C2C2C] hover:border-[#8B1A1A]/60 transition-all duration-500 overflow-hidden group cursor-pointer"
      onClick={() => navigate(`/lore/${post.slug}`)}
    >
      {/* Cover Image */}
      <div className="relative h-52 overflow-hidden">
        <img
          src={post.image}
          alt={`${post.title} lore entry cover`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />

        {/* Character Tag */}
        <div className="absolute top-4 left-4">
          <span
            className="font-['Share_Tech_Mono'] text-xs tracking-[0.25em] uppercase px-3 py-1.5 border"
            style={{ color: post.characterColor, borderColor: `${post.characterColor}50`, backgroundColor: `${post.characterColor}15` }}
          >
            {post.characterTag}
          </span>
        </div>

        {/* Entry Number */}
        <div className="absolute top-4 right-4">
          <span className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-widest">
            {post.entryNumber}
          </span>
        </div>

        {/* Timeline Badge */}
        <div className="absolute bottom-4 left-4">
          <span className="font-['Share_Tech_Mono'] text-[#777] text-xs tracking-wide">
            {post.timeline}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mb-3">
          <div
            className="font-['Share_Tech_Mono'] text-xs tracking-widest uppercase mb-1"
            style={{ color: post.characterColor }}
          >
            {post.character} &mdash; {post.characterAlias}
          </div>
          <h3 className="font-['Special_Elite'] text-white text-2xl mb-1 group-hover:text-[#C8922A] transition-colors duration-300">
            {post.title}
          </h3>
          <div className="font-['Rajdhani'] text-[#555] text-sm tracking-wide">
            {post.subtitle}
          </div>
        </div>

        <p className="font-['Rajdhani'] text-[#888] text-sm leading-relaxed line-clamp-3 mb-5">
          {post.excerpt}
        </p>

        <div className="flex items-center justify-between">
          <span className="font-['Share_Tech_Mono'] text-[#444] text-xs tracking-wide">
            {post.readTime}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/lore/${post.slug}`); }}
            className="flex items-center gap-2 font-['Share_Tech_Mono'] text-xs tracking-[0.15em] uppercase border px-3 py-2 transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ color: post.characterColor, borderColor: `${post.characterColor}40` }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = `${post.characterColor}15`;
              (e.currentTarget as HTMLButtonElement).style.borderColor = post.characterColor;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "transparent";
              (e.currentTarget as HTMLButtonElement).style.borderColor = `${post.characterColor}40`;
            }}
          >
            Read Entry
            <i className="ri-arrow-right-line text-sm" />
          </button>
        </div>
      </div>
    </article>
  );
}
